import * as M from '@mantine/core';

import {
  AI_AGENT_MANUAL_UPLOAD_CHANNELS,
  AI_AGENT_TASK_STATUSES,
  manualUploadChannelOptions,
  taskStatusOptions,
} from '../../../../../../../shared/constants/aiAgentOptions';

import { InfoField } from '../../../../../../ui/infoField/InfoField';
import CustomSearch from '../../../../../../ui/search/customSearch';

import type { KeyboardEvent, MouseEvent, RefObject } from 'react';
import type { AiAgentManualSearchType, IAiAgentManualFilterForm } from '../types';
import { IModelOption } from '../../../Common/hooks/useModelOptions';

interface IAiAgentManualFilterSectionProps {
  filterForm: IAiAgentManualFilterForm;
  modelOptions: IModelOption[];
  searchType: AiAgentManualSearchType;
  inputRef: RefObject<HTMLInputElement>;
  onChangeFilterForm: (nextFilter: Partial<IAiAgentManualFilterForm>) => void;
  onChangeSearchType: (value: string | null) => void;
  handleSearch: (event: MouseEvent<HTMLButtonElement> | KeyboardEvent<HTMLInputElement>) => void;
  resetSearchInput: () => void;
  onReset: () => void;
}

export default function AiAgentManualFilterSection(props: IAiAgentManualFilterSectionProps) {
  const {
    filterForm,
    modelOptions,
    searchType,
    inputRef,
    onChangeFilterForm,
    onChangeSearchType,
    handleSearch,
    resetSearchInput,
    onReset,
  } = props;

  const onChangeUploadChannel = (value: IAiAgentManualFilterForm['types'][number], checked: boolean) => {
    onChangeFilterForm({
      types: checked ? [...filterForm.types, value] : filterForm.types.filter((type) => type !== value),
    });
  };

  const onChangeTaskStatus = (value: IAiAgentManualFilterForm['statuses'][number], checked: boolean) => {
    onChangeFilterForm({
      statuses: checked ? [...filterForm.statuses, value] : filterForm.statuses.filter((status) => status !== value),
    });
  };

  return (
    <M.Box
      style={{
        borderTop: '1px solid var(--mantine-color-gray-3)',
        borderLeft: '1px solid var(--mantine-color-gray-3)',
      }}
    >
      <M.SimpleGrid cols={2} spacing={0} verticalSpacing={0} style={{ gridTemplateColumns: '180px minmax(0, 1fr)' }}>
        <InfoField label="등록 서비스" labelAlign="left" contentAlign="left">
          <M.Flex gap="lg" wrap="wrap">
            <M.Checkbox
              label="전체"
              size="sm"
              checked={filterForm.types.length === 0}
              onChange={() => onChangeFilterForm({ types: [] })}
            />
            {manualUploadChannelOptions
              .filter((option) => option.value !== AI_AGENT_MANUAL_UPLOAD_CHANNELS.ALL)
              .map((option) => (
                <M.Checkbox
                  key={option.value}
                  label={option.label}
                  size="sm"
                  checked={filterForm.types.includes(option.value)}
                  onChange={(event) => onChangeUploadChannel(option.value, event.currentTarget.checked)}
                />
              ))}
          </M.Flex>
        </InfoField>
        <InfoField label="진행 상태" labelAlign="left" contentAlign="left">
          <M.Flex gap="lg" wrap="wrap">
            <M.Checkbox
              label="전체"
              size="sm"
              checked={filterForm.statuses.length === 0}
              onChange={() => onChangeFilterForm({ statuses: [] })}
            />
            {taskStatusOptions
              .filter((option) => option.value !== AI_AGENT_TASK_STATUSES.ALL)
              .map((option) => (
                <M.Checkbox
                  key={option.value}
                  label={option.label}
                  size="sm"
                  checked={filterForm.statuses.includes(option.value)}
                  onChange={(event) => onChangeTaskStatus(option.value, event.currentTarget.checked)}
                />
              ))}
          </M.Flex>
        </InfoField>
        <InfoField label="기계모델" labelAlign="left" contentAlign="left">
          <M.Select
            searchable
            data={modelOptions}
            value={filterForm.modelId}
            onChange={(modelId) => {
              if (modelId) {
                onChangeFilterForm({ modelId });
              }
            }}
          />
        </InfoField>
        <InfoField label="상세 검색" labelAlign="left" contentAlign="left">
          <M.Flex gap="sm" align="center" wrap="wrap" w="100%">
            <CustomSearch
              resetSearch={onReset}
              resetSearchInput={resetSearchInput}
              searchType={searchType}
              setSearchType={onChangeSearchType}
              inputRef={inputRef}
              handleSearch={handleSearch}
              searchOptions={[
                {
                  value: 'manualName',
                  label: '매뉴얼명',
                },
              ]}
              placeholder="검색어를 입력하세요."
            />
          </M.Flex>
        </InfoField>
      </M.SimpleGrid>
    </M.Box>
  );
}
